import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Button, CircularProgress, Typography, Alert } from '@mui/material';
import { onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, provider } from 'providers/utils';

const LoginPage = () => {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const finish = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirect');
    navigate(redirectTo || '/', { replace: true });
  }, [location.search, navigate]);

  // Navigate away immediately if a session already exists
  useEffect(() => {
    if (auth.currentUser) {
      finish();
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) finish();
    });
    return () => unsub();
  }, [finish]);

  const handleLogin = useCallback(() => {
    setError(null);
    setIsSigningIn(true);
    signInWithPopup(auth, provider)
      .then(() => {
        setIsSigningIn(false); // navigation handled by auth listener
      })
      .catch((e: any) => {
        setIsSigningIn(false);
        if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') {
          setError('Sign-in popup was closed before completion. Please try again.');
          return;
        }
        if (e?.code === 'auth/popup-blocked') {
          setError('Popup was blocked by the browser. Allow popups and retry.');
          return;
        }
        if (e?.code === 'auth/operation-not-supported-in-this-environment') {
          setError('Popup sign-in not supported in this environment. Try a standard browser.');
          return;
        }
        setError(e?.message || 'Login failed');
      });
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Card sx={{ width: 380, p: 1, backdropFilter: 'blur(2px)' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5" align="center">
            Admin Portal
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {isSigningIn ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress size={30} />
              <Typography variant="body2">Signing in...</Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogin}
              disabled={isSigningIn}
              data-testid="oidc-login-button"
            >
              Sign in with Okta
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
